import { Avatar } from '@/components/common/avatar';
import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAppVersion } from '@/hooks/use-app-version';
import { useNavigation } from '@/hooks/use-navigation';
import { useSettings } from '@/hooks/use-settings';
import { SUPPORTED_LOCALES } from '@/i18n/i18n';
import { useRevenuecat } from '@/lib/revenuecat/provider';
import { cn } from '@/lib/utils';
import { useCanRecord } from '@/lib/utils/can-record-check';
import { extractNameFromEmail } from '@/lib/utils/string';
import { Capacitor } from '@capacitor/core';
import { useAuthActions } from '@convex-dev/auth/react';
import { convexQuery } from '@convex-dev/react-query';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, Loader2, LogOutIcon, Save } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '~/convex/_generated/api';

export const Settings = () => {
  const { t } = useTranslation();
  const { signOut } = useAuthActions();
  const { data: currentUser } = useQuery(convexQuery(api.user.currentUser, {}));
  const { form, isSaving, isDirty, handleSave } = useSettings();
  const { data: versionInfo } = useAppVersion();
  const { numberOfRecordings, reportLimit, isUnlimitedReports } =
    useCanRecord();
  const { handleNavigate } = useNavigation();

  const {
    isSubscribed,
    showPaywall,
    manageSubscriptions,
    restorePurchases,
    isLoading,
    offerings,
  } = useRevenuecat();

  const subscriptionPrice = useMemo(() => {
    if (isSubscribed || !offerings?.current) return null;
    const pkg =
      offerings.current.monthly ||
      offerings.current.annual ||
      offerings.current.availablePackages[0];

    if (!pkg) return null;

    return t(`settings:subscription.packages.${pkg.identifier}`, {
      price: pkg.priceString,
      defaultValue: `${pkg.title} (${pkg.priceString})`,
    });
  }, [isSubscribed, offerings, t]);

  const displayName =
    currentUser?.name || extractNameFromEmail(currentUser?.email);

  const handleSignOut = async () => {
    await signOut();
    handleNavigate('/', true);
  };

  return (
    <>
      {/* Profile Section */}
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full items-center gap-4 bg-white py-4 md:pt-6">
        <Avatar
          avatarUrl={currentUser?.image}
          name={currentUser?.name}
          email={currentUser?.email}
          size="md"
          className="shrink-0"
        />
        <div className="flex flex-1 flex-col overflow-x-hidden">
          <h2 className="w-full truncate text-lg font-semibold text-gray-900">
            {displayName}
          </h2>
          <button className="w-full truncate text-left text-sm font-light text-gray-600">
            {currentUser?.email}
          </button>
        </div>
      </ResponsiveContainer>

      {/* Settings Content */}
      <ResponsiveContainer
        className={cn(
          'flex min-h-0 w-full grow flex-col gap-3 overflow-y-auto md:pb-6',
          isDirty && 'pb-3 md:pb-4',
        )}>
        <SettingsCard title={t('settings:appSettings')}>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-light text-gray-500">
              {t('settings:changeLanguage')}
            </p>
            <Select
              value={form.watch('language')}
              onValueChange={(value) =>
                form.setValue('language', value, { shouldDirty: true })
              }>
              <SelectTrigger className="bg-white text-base shadow-none">
                <SelectValue placeholder={t('settings:changeLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LOCALES.map((locale) => (
                  <SelectItem key={locale} value={locale}>
                    {t(`home:languages.${locale}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </SettingsCard>

        <SettingsCard title={t('settings:subscription.title')}>
          <div className="flex flex-col gap-4">
            {isSubscribed ? (
              <p>
                {t('settings:subscription.subscribedPrefix')}
                <span className="inline-flex items-center font-bold">
                  <span className="text-primary brightness-90">Voice</span>
                  <span className="text-voicefill-black mr-1">Fill</span>
                  <span className="bg-primary/70 py-0.1 text-voicefill-black rounded-md px-2 text-xs drop-shadow-xs">
                    Pro
                  </span>
                </span>
                {t('settings:subscription.subscribedSuffix')}
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                <p>
                  {t('settings:subscription.used', {
                    count: numberOfRecordings,
                    max: isUnlimitedReports ? '∞' : (reportLimit ?? 0),
                  })}
                </p>
                <Progress
                  value={numberOfRecordings ?? 0}
                  max={reportLimit ?? Math.max(numberOfRecordings ?? 0, 1)}
                  className="h-2 w-full"
                />
              </div>
            )}
            <Button
              onClick={() =>
                isSubscribed ? manageSubscriptions() : showPaywall()
              }
              disabled={isLoading}
              loading={isLoading}
              variant="outline"
              className="flex justify-between px-4! py-3 text-base font-normal">
              {isSubscribed
                ? t('settings:subscription.manage')
                : subscriptionPrice}
              <ArrowRightIcon className="opacity-50" />
            </Button>
            {Capacitor.getPlatform() !== 'web' && !isSubscribed && (
              <Button
                onClick={restorePurchases}
                variant="outline"
                className="flex w-fit justify-start font-medium"
                size="sm">
                {t('settings:subscription.restorePurchase')}
              </Button>
            )}
            <div className="flex w-full justify-stretch gap-2">
              <a
                href="https://medical.voicefill.ai/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer">
                <Button
                  variant="link"
                  size="sm"
                  className="text-black underline">
                  {t('settings:subscription.privacyPolicy')}
                </Button>
              </a>
              <a
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer">
                <Button
                  variant="link"
                  size="sm"
                  className="text-black underline">
                  {t('settings:subscription.termsOfService')}
                </Button>
              </a>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title={t('settings:transcriptionSettings')}>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-light text-gray-500">
              {t('settings:context')}
            </p>
            <Textarea
              {...form.register('context')}
              placeholder={t('settings:contextPlaceholder')}
              className="bg-white shadow-none"
              rows={5}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-light text-gray-500">
              {t('settings:specialization')}
            </p>
            <Input
              {...form.register('specialization')}
              placeholder={t('settings:specializationPlaceholder')}
              className="bg-white shadow-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-light text-gray-500">
              {t('settings:clinic')}
            </p>
            <Input
              {...form.register('clinic')}
              placeholder={t('settings:clinicPlaceholder')}
              className="bg-white shadow-none"
            />
          </div>
        </SettingsCard>

        {/* Version information */}
        {Capacitor.getPlatform() !== 'web' && versionInfo && (
          <SettingsCard title={t('settings:versionInfo')}>
            <p className="text-xs font-light text-gray-500">
              {t('settings:versionInfoDescription')}
            </p>
            <VersionRow
              label={t('settings:nativeVersion')}
              value={versionInfo?.nativeVersion ?? t('settings:unknown')}
            />
            <VersionRow
              label={t('settings:buildNumber')}
              value={versionInfo?.buildNumber ?? t('settings:unknown')}
            />
            <VersionRow
              label={t('settings:bundleVersion')}
              value={versionInfo?.bundleVersion ?? t('settings:unknown')}
            />
          </SettingsCard>
        )}

        <Button
          variant="destructive"
          className="md:hidden"
          onClick={handleSignOut}>
          <LogOutIcon className="size-4" />
          {t('settings:signOut')}
        </Button>

        <div className="flex justify-center py-8">
          <img
            src="/logo-text.svg"
            alt="Voicefill Medical"
            className="h-8 w-auto max-w-[180px] opacity-90 select-none"
          />
        </div>
      </ResponsiveContainer>

      <AnimatePresence>
        {isDirty && (
          <ResponsiveContainer>
            <motion.div
              className="w-full border-t pt-3 md:py-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="w-full">
                {isSaving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <>
                    <Save className="size-4" />
                    {t('templates:save')}
                  </>
                )}
              </Button>
            </motion.div>
          </ResponsiveContainer>
        )}
      </AnimatePresence>
    </>
  );
};

const SettingsCard = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-gray-100 px-5 py-4">
      <h1 className="text-xl font-semibold">{title}</h1>
      {children}
    </div>
  );
};

const VersionRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-white px-4 py-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
};
